import { useState, createContext } from 'react'
import type { FocusEvent, KeyboardEvent, Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'

import type { InputProps } from './Input'

import { Color } from '../../theme/color'

type InputContainerProps = InputProps & { isFocus: boolean }

interface InputContextValue {
  isFocus: boolean
  setIsFocus: Dispatch<SetStateAction<InputContextValue['isFocus']>>
  handleFocus: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleBlur: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

export const InputContext = createContext<InputContextValue>({} as InputContextValue)

export function Container({ $shape = 'box', block = true, $borderColor, multiline, children, ...props }: InputProps) {
  const [isFocus, setIsFocus] = useState<boolean>(false)

  const handleFocus = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.onFocus?.(e)
    setIsFocus(true)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.preventDefault()
    props.onBlur?.(e)
    setIsFocus(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.onKeyDown?.(e)
  }

  const providerValue = { isFocus, setIsFocus, handleFocus, handleBlur, handleKeyDown }

  const shapeContainerProps = {
    $shape,
    $borderColor,
    block,
    multiline,
    isFocus,
  }

  return (
    <InputContext.Provider value={providerValue}>
      {$shape === 'box' && <BlockContainer {...shapeContainerProps}>{children}</BlockContainer>}
      {$shape === 'line' && <LineContainer {...shapeContainerProps}>{children}</LineContainer>}
    </InputContext.Provider>
  )
}

const BlockContainer = styled.div<InputContainerProps>`
  ${({ block }) => (block ? 'display: flex; width: 100%;' : 'display: inline-flex;')}
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  padding: 16px;
  border: 1px solid;
  border-color: ${({ $borderColor, isFocus }) =>
    $borderColor ? Color[$borderColor] : isFocus ? Color.primary_5 : Color.gray_3};
  border-radius: 8px;
  > *:not(input) {
    flex-shrink: 0;
  }
  > input,
  textarea {
    width: 100%;
    padding: 0;
    outline: none;
    border: none;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: ${Color.text_primary};
    caret-color: ${Color.primary_5};
    &::placeholder {
      color: ${Color.gray_4};
    }
    &:disabled {
      background-color: transparent;
    }
  }
`

const LineContainer = styled.div<InputContainerProps>`
  display: ${({ block }) => (block ? 'flex' : 'inline-flex')};
  align-items: center;
  justify-content: space-between;
  position: relative;
  padding: 8px 0 10px;
  &::after {
    display: block;
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 100%;
    border-bottom: 2px solid;
    border-color: ${({ isFocus }) => (isFocus ? Color.primary_5 : Color.line)};
    transform: translateX(-50%);
  }
  > input {
    outline: none;
    border: none;
    font-size: 18px;
    font-weight: 600;
    color: ${Color.text_primary};
    caret-color: ${Color.primary_5};
    &::placeholder {
      color: ${Color.gray_4};
    }
  }
`
